import {
  convertJalaliToGregorian,
  convertToJalaliDate,
  loadAllReportDates,
  loadAllScenarios,
} from './common.util';
import i18n from 'locales/fa.json';
import {
  NavDataType,
  NavRawDataType,
  NavType,
  OverViewType,
  StockResultType,
  StockType,
} from 'typings/nav.type';

export const checkCombinedScenario = (navData: NavRawDataType): boolean =>
  navData.all_report_dates[navData.report_date].scenario_names.find(
    (item) => item.id === navData.id,
  ).is_child;

// to save default ids and use them for getting default values
const defaultIds = (
  data: Record<
    string,
    {
      has_default_analysis: boolean;
      scenario_names: { is_default?: boolean; id }[];
    }
  >,
): { date: string; id: string }[] => {
  const result = Object.keys(data).map((key) => ({
    date: convertToJalaliDate(key),
    id: !data[key].has_default_analysis
      ? data[key].has_default_analysis // it could be null or false
      : data[key].scenario_names.find((item) => item.is_default).id,
  }));
  return result;
};

export const getStockData = (
  data: { manual_valuation: number; companies: StockType[] },
  analysis,
  isCombined: boolean,
): StockType[] => {
  const newData = [];
  const otherAnalysis = analysis.find((item) => item.is_other); // row with is_other true

  data.companies.forEach((item) => {
    const analysisItem = analysis?.find((key) => key.portfolio_id === item.id);
    const order = analysis?.findIndex((key) => key.portfolio_id === item.id); // use for sending data to back

    newData.push({
      ...item,
      ...analysisItem,
      edited: [],
      isCombined,
      order,
      earning_per_share: analysisItem?.earning_per_share_helper || '0',
      percentage: analysisItem.percentage_helper || 0,
      companyProfit: analysisItem.total_dividend_helper,
    });
  });
  newData.push({
    edited: [],
    company_name: i18n.nav.other,
    valuation: data.manual_valuation,
    excess_value: data.manual_valuation,
    companyProfit: Number(otherAnalysis.total_dividend),
    company_id: 'manual_valuation',
  });

  return newData;
};

export const getNavData = (
  data: NavRawDataType,
  overviewData: OverViewType,
): NavDataType => ({
  id: data.id,
  navId: data.id,
  symbol: data.security_symbol,
  name: data.security_name,
  lastPrice: overviewData.last_price,
  status: overviewData.status,
  lastPricePercentageChange: overviewData.last_price_percentage_changes,
  isDefault: data.is_default,
  security: data.security,
  symbol12Chars: data.security_symbol_12chars,
  scenarioName: data.scenario_name,
  defaultIds: defaultIds(data.all_report_dates),
  allScenarios: loadAllScenarios(data),
  allReportDates: loadAllReportDates(data),
  reportDate: convertToJalaliDate(data.report_date),
  equity: overviewData.equity,
  sharesCount: overviewData.shares_count,
  ...data,
});

export const getNodeFormula = (data: NavDataType, name: string): string =>
  data.user_formulas?.[name]?.toString() || data[name]?.toString();

export const isManualValuation = (id: string | number): boolean =>
  id === 'manual_valuation';

// on nav save, we should not send this keys
export const removeExtraKey = (data: object): object => {
  [
    'earning_per_share_helper',
    'total_dividend_helper',
    'percentage_helper',
  ].forEach((e) => delete data[e]); // eslint-disable-line
  return data;
};

export const normalizeNavSave = (
  stockPortfolio: StockType[],
  outOfExchangePortfolio: StockType[],
  navData: NavDataType,
  // scenarioName?: string,
): object => {
  const { security, reportDate, id } = navData;
  const stockOther = navData.stock_portfolios.find((key) => key.is_other);
  const outOfExchangeOther = navData.out_of_exchange_portfolios.find(
    (key) => key.is_other,
  );
  removeExtraKey(stockOther);
  removeExtraKey(outOfExchangeOther);

  const data = {
    scenarios: outOfExchangePortfolio
      .filter(
        (item) => !isManualValuation(item.company_id) && item.id !== 'sum',
      )
      .map((item) => ({
        company: item.company_id,
        formula: item.valuation.toString(),
      })),

    stock_portfolios_last_price: stockPortfolio
      .filter(
        (item) => !isManualValuation(item.company_id) && item.id !== 'sum',
      )
      .map((item) => ({
        company: item.company_id,
        formula: item.last_price.toString(),
      })),

    stock_valuation:
      stockPortfolio.find((item) => isManualValuation(item.company_id))
        .valuation || '0',
    out_of_exchange_valuation:
      outOfExchangePortfolio.find((item) => isManualValuation(item.company_id))
        .valuation || '0',

    navdamental_analysis: {
      security,
      id,
      report_date: convertJalaliToGregorian(reportDate),
      interest_income: navData.interest_income,
      gain_from_sale_investments: navData.gain_from_sale_investments,
      gain_from_changes_in_fair_values_of_investments:
        navData.gain_from_changes_in_fair_values_of_investments,
      other_operating_revenue: navData.other_operating_revenue,
      sales_administrative_and_general_expenses:
        navData.sales_administrative_and_general_expenses,
      other_operating_expenses: navData.other_operating_expenses,
      interest_expenses: navData.interest_expenses,
      income_taxes: navData.income_taxes,
      net_miscellaneous_revenue: navData.net_miscellaneous_revenue,
      net_income: getNodeFormula(navData, 'net_income'),
      operating_revenue: getNodeFormula(navData, 'operating_revenue'),
      operating_expenses: getNodeFormula(navData, 'operating_expenses'),
      operating_income: getNodeFormula(navData, 'operating_income'),
      capital: navData.capital.toString(),
      scenario_name: navData.scenarioName,
      dividend_income: '',
      stock_portfolios: stockPortfolio
        .sort((a, b) => a.order - b.order)
        .filter(
          (item) => !isManualValuation(item.company_id) && item.id !== 'sum',
        )
        .map((item) => ({
          volume: item.volume,
          company: item.company,
          is_child: item.is_child,
          percentage: Number(item.percentage),
          portfolio_id: item.portfolio_id,
          financial_year: item.financial_year,
          total_dividend: item.total_dividend,
          is_accrued: item.is_accrued,
          is_buy_after_agm: item.is_buy_after_agm,
          is_other: item.is_other,
          earning_per_share: item.earning_per_share.toString(),
        }))
        .concat({
          ...stockOther,
          total_dividend: (
            stockPortfolio.find((item) => isManualValuation(item.company_id))
              .companyProfit || 0
          ).toString(),
        } as any), // eslint-disable-line
      out_of_exchange_portfolios: outOfExchangePortfolio
        .sort((a, b) => a.order - b.order)
        .filter(
          (item) => !isManualValuation(item.company_id) && item.id !== 'sum',
        )
        .map((item) => ({
          volume: item.volume,
          company: item.company,
          is_child: item.is_child,
          percentage: Number(item.percentage),
          portfolio_id: item.portfolio_id,
          is_accrued: item.is_accrued,
          is_buy_after_agm: item.is_buy_after_agm,
          is_other: item.is_other,
          financial_year: item.financial_year,
          total_dividend: item.total_dividend,
          earning_per_share: item.earning_per_share.toString(),
        }))
        .concat({
          ...outOfExchangeOther,
          total_dividend: (
            outOfExchangePortfolio.find((item) =>
              isManualValuation(item.company_id),
            ).companyProfit || 0
          ).toString(),
        } as any), // eslint-disable-line
    },
  };

  return data;
};

export const updateDividend = (data: StockType[], analysis): StockType[] => {
  const newData = [];

  data.forEach((item) => {
    const analysisItem = analysis?.find((key) => key.portfolio_id === item.id);
    newData.push({
      ...item,
      companyProfit:
        item.company_id === 'manual_valuation'
          ? item.companyProfit
          : analysisItem?.total_dividend_helper || 0,
    });
  });
  return newData;
};

// Nav per share is calculated with the following formula:
// nav = (stockExcessValue + outOfExchangeExcessValue + equity) * 10^6 / sharesCount
// We output nav per share, price per nav, and both stock and outOfExchange valuation and excessValue
export const calculateNav = (
  stockPortfolio: StockType[],
  outOfExchangePortfolio: StockType[],
  overview: NavDataType,
): NavType => {
  if (!overview || !stockPortfolio) {
    return {
      stockValuation: 0,
      stockExcessValue: 0,
      outOfExchangeValuation: 0,
      outOfExchangeExcessValue: 0,
      navPerShare: 0,
      pricePerNav: 0,
    };
  }

  // reducer to get the sum of valuation and excessValue
  const reducer = (acc: StockResultType, item: StockType): StockResultType => ({
    valuation: acc.valuation + item.valuation,
    excessValue: acc.excessValue + item.excess_value,
  });

  // Calculates the total stock valuation and stock excessValue
  const stockTotal = stockPortfolio?.reduce(reducer, {
    valuation: 0,
    excessValue: 0,
  });

  // Calculates the total outOfExchange valuation and stock excessValue
  // OutOfExchange manual valuations (i.e., the `others` field) is already present in the outOfExchangePortfolio,
  // so we don't need to add it manually
  const outOfExchangeTotal = outOfExchangePortfolio?.reduce(reducer, {
    valuation: 0,
    excessValue: 0,
  });

  // Formula to calculate nav
  const navPerShare =
    ((overview.equity +
      stockTotal.excessValue +
      (outOfExchangeTotal?.excessValue ?? 0)) *
      1000000) /
    overview.sharesCount;

  return {
    stockValuation: stockTotal.valuation,
    stockExcessValue: stockTotal.excessValue,
    outOfExchangeValuation: outOfExchangeTotal?.valuation,
    outOfExchangeExcessValue: outOfExchangeTotal?.excessValue,
    navPerShare,
    pricePerNav: (overview.lastPrice / navPerShare) * 100,
  };
};
